import type {
  ArticleHeader,
  ArticleV2,
  HeaderContent
} from 'types/capi/article';
import type { Author, CoreImageData } from 'types/capi/shared';

import Image from '~/components/Image';
import ImageCaption from '~/components/ImageCaption';
import Link from '~/components/Link';

import { formatDate } from './HeaderFooter';

type Props = {
  articleType?: ArticleV2['article_type'];
  author: Author;
  header?: ArticleHeader;
  images: CoreImageData[];
  publishedDate?: number;
  title: string;
};

export default function HeaderImage({
  header,
  images,
  articleType,
  title,
  author,
  publishedDate
}: Props) {
  if (header) {
    return (
      <ResponsiveHeaderImage
        header={header}
        title={title}
        author={author}
        publishedDate={publishedDate}
      />
    );
  }

  // Legacy fallback: no header data, use core_images
  if (articleType === 'news') {
    return <PaddedHeaderImage images={images} />;
  }

  return <FullWidthHeaderImage images={images} />;
}

function ResponsiveHeaderImage({
  header,
  title,
  author,
  publishedDate
}: {
  author: Author;
  header: ArticleHeader;
  publishedDate?: number;
  title: string;
}) {
  const { desktop, mobile } = header;

  const desktopHasImage = desktop.template !== 'title_only';
  const mobileHasImage = mobile.template !== 'title_only';

  if (!desktopHasImage && !mobileHasImage) return null;

  const containerVisibility = !desktopHasImage
    ? 'md:hidden'
    : !mobileHasImage
      ? 'hidden md:block'
      : '';

  const primaryImage = mobileHasImage ? mobile.image : desktop.image;
  const responsiveOptions = desktopHasImage
    ? { md: { src: desktop.image.url } }
    : undefined;

  const desktopOverlay = desktop.template === 'title_over_image';
  const mobileOverlay = mobile.template === 'title_over_image';
  const overlayClass =
    desktopOverlay && mobileOverlay
      ? 'flex'
      : desktopOverlay
        ? 'hidden md:flex'
        : mobileOverlay
          ? 'flex md:hidden'
          : null;

  const widthClass =
    desktop.template !== 'title_over_image' && desktop.ratio === '16:9'
      ? 'max-w-desktop mx-auto desktop:py-6'
      : '';

  return (
    <div className={`${widthClass} ${containerVisibility}`}>
      <div className="relative w-full overflow-hidden">
        <Image
          alt={primaryImage.alt}
          src={primaryImage.url}
          display="full-bleed"
          responsiveOptions={responsiveOptions}
          progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
          className="w-full object-cover"
          loading="eager"
        />

        {overlayClass && (
          <div
            className={`absolute inset-0 ${overlayClass} items-center justify-center bg-black/[0.48] px-4 py-6 md:px-8`}
          >
            <OverlayMetadata
              title={title}
              author={author}
              publishedDate={publishedDate}
            />
          </div>
        )}

        <ResponsiveCaption desktop={desktop} mobile={mobile} />
      </div>
    </div>
  );
}

function ResponsiveCaption({
  desktop,
  mobile
}: {
  desktop: HeaderContent;
  mobile: HeaderContent;
}) {
  const desktopCaption =
    desktop.template !== 'title_only' ? desktop.image.caption : undefined;
  const mobileCaption =
    mobile.template !== 'title_only' ? mobile.image.caption : undefined;

  if (desktopCaption === mobileCaption) {
    return <ImageCaption caption={desktopCaption} />;
  }

  return (
    <>
      {mobileCaption && (
        <ImageCaption caption={mobileCaption} className="md:hidden" />
      )}
      {desktopCaption && (
        <ImageCaption caption={desktopCaption} className="hidden md:block" />
      )}
    </>
  );
}

function OverlayMetadata({
  title,
  author,
  publishedDate
}: {
  author: Author;
  publishedDate?: number;
  title: string;
}) {
  return (
    <div className="flex w-full max-w-desktop flex-col items-center gap-2 text-center lg:gap-4">
      <div className="text-hero text-balance text-white">{title}</div>
      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-grey-panel">
        {publishedDate && (
          <>
            <span>{formatDate(publishedDate)}</span>
            <span className="select-none">•</span>
          </>
        )}
        <Link to={author.path} external className="underline hover:text-white">
          {author.name}
        </Link>
      </div>
    </div>
  );
}

// Legacy components (used when header data is not available)

function FullWidthHeaderImage({ images }: { images: CoreImageData[] }) {
  const { desktop, main } = getFullWidthImages(images);

  return (
    <div className="relative">
      <Image
        alt={desktop.alt}
        src={desktop.url}
        display="full-bleed"
        responsiveOptions={{
          md: { src: desktop.url },
          xs: { src: main.url }
        }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
        className="z-0 w-full object-cover xs:h-[141px] md:h-[288px] lg:h-[384px] xl:h-[540px]"
        loading="eager"
      />
      {main.caption && (
        <span
          className="absolute bottom-3 left-3 z-10 text-sm font-medium text-white"
          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}
        >
          {main.caption}
        </span>
      )}
    </div>
  );
}

function PaddedHeaderImage({ images }: { images: CoreImageData[] }) {
  const image = images.find((img) => img.ratio === '16:9')!.image;

  return (
    <div className="flex flex-col items-center bg-grey-tile md:px-10 md:pt-6">
      <Image
        alt={image.alt}
        src={image.url}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
        className="w-full max-w-desktop"
        loading="eager"
      />
      {image.caption && (
        <span className="mt-2 block text-xs font-semibold text-grey-hide">
          {image.caption}
        </span>
      )}
    </div>
  );
}

function getFullWidthImages(images: CoreImageData[]) {
  const main = images.find((img) => img.ratio === '16:9')!.image;
  const desktop = images.find((img) => img.ratio === '8:3')!.image;

  return { desktop, main };
}
